import * as Sentry from "@sentry/react";
import { Switch, Route, Router as WouterRouter, Redirect } from "wouter";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { OnboardingModal } from "@/components/onboarding-modal";
import { AccountPromptModal } from "@/components/account-prompt-modal";
import { PaywallModal } from "@/components/paywall-modal";
import { useSyncExternalStore } from "react";
import { getDeviceFingerprint } from "@/lib/fingerprint";
import { setFingerprintResolver } from "@workspace/api-client-react/custom-fetch";
import { AppBootstrap } from "@/components/app-bootstrap";

setFingerprintResolver(getDeviceFingerprint);

import AppLayout from "./components/layout/app-layout";
import Dashboard from "./pages/dashboard";
import VoiceTraining from "./pages/voice-training";
import VoiceStyle from "./pages/voice-style";
import PostGenerator from "./pages/post-generator";
import CreatorTools from "./pages/creator-tools";
import Studio from "./pages/studio";
import IdeaVault from "./pages/idea-vault";
import Settings from "./pages/settings";
import Integrations from "./pages/integrations";
import SmartReply from "./pages/smart-reply";
import Login from "./pages/login";
import SignupPage from "./pages/signup";
import ForgotPassword from "./pages/forgot-password";
import ResetPassword from "./pages/reset-password";
import VerifyEmail from "./pages/verify-email";
import SubscriptionSuccess from "./pages/subscription-success";
import GiftRedeemPage from "./pages/gift-redeem";
import GiftSuccessPage from "./pages/gift-success";
import Privacy from "./pages/privacy";
import Terms from "./pages/terms";
import Admin from "./pages/admin";
import LandingPage from "./pages/landing";
import PricingPage from "./pages/pricing";
import AboutPage from "./pages/about";
import BlogPage from "./pages/blog";
import BlogPostPage from "./pages/blog-post";
import NotFound from "@/pages/not-found";
import StartPage from "./pages/start";
import { useAppStore } from "@/store";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      refetchOnWindowFocus: false,
    },
  },
});

function useStoreHydrated() {
  return useSyncExternalStore(
    useAppStore.persist.onFinishHydration,
    () => useAppStore.persist.hasHydrated(),
    () => false
  );
}

function AuthGate({ children }: { children: React.ReactNode }) {
  const { hasAccount, authToken } = useAppStore();
  const hydrated = useStoreHydrated();

  if (!hydrated) return null;

  if (hasAccount && !authToken) {
    return <Redirect to="/login" />;
  }

  return <>{children}</>;
}

function HardPaywallGate() {
  const { postsGeneratedCount, isSubscribed } = useAppStore();
  const isHardWall = postsGeneratedCount >= 15 && !isSubscribed;
  return <PaywallModal show={isHardWall} onDismiss={() => {}} hard />;
}

function HomeRoute() {
  const { authToken, hasAccount } = useAppStore();
  const hydrated = useStoreHydrated();

  if (!hydrated) return null;

  if (authToken) {
    return (
      <AuthGate>
        <AppBootstrap />
        <HardPaywallGate />
        <OnboardingModal />
        <AccountPromptModal />
        <AppLayout>
          <Dashboard />
        </AppLayout>
      </AuthGate>
    );
  }

  if (hasAccount) {
    return <Redirect to="/login" />;
  }

  return (
    <>
      <OnboardingModal />
      <LandingPage />
    </>
  );
}

function AppRouter() {
  return (
    <Switch>
      <Route path="/" component={HomeRoute} />
      <Route path="/login" component={Login} />
      <Route path="/signup" component={SignupPage} />
      <Route path="/gift/redeem" component={GiftRedeemPage} />
      <Route path="/gift/success" component={GiftSuccessPage} />
      <Route path="/forgot-password" component={ForgotPassword} />
      <Route path="/reset-password" component={ResetPassword} />
      <Route path="/verify-email" component={VerifyEmail} />
      <Route path="/subscription-success" component={SubscriptionSuccess} />
      <Route path="/privacy" component={Privacy} />
      <Route path="/terms" component={Terms} />
      <Route path="/start" component={StartPage} />
      <Route path="/pricing" component={PricingPage} />
      <Route path="/about" component={AboutPage} />
      <Route path="/blog" component={BlogPage} />
      <Route path="/blog/:slug" component={BlogPostPage} />
      <Route>
        <AuthGate>
          <AppBootstrap />
          <HardPaywallGate />
          <OnboardingModal />
          <AccountPromptModal />
          <AppLayout>
            <Switch>
              <Route path="/training" component={VoiceTraining} />
              <Route path="/voice-style" component={VoiceStyle} />
              <Route path="/generator" component={PostGenerator} />
              <Route path="/studio" component={Studio} />
              <Route path="/tools" component={CreatorTools} />
              <Route path="/reply" component={SmartReply} />
              <Route path="/vault" component={IdeaVault} />
              <Route path="/settings" component={Settings} />
              <Route path="/integrations" component={Integrations} />
              <Route path="/admin" component={Admin} />
              <Route component={NotFound} />
            </Switch>
          </AppLayout>
        </AuthGate>
      </Route>
    </Switch>
  );
}

function SentryFallback() {
  return (
    <div className="flex items-center justify-center min-h-screen p-8">
      <div className="text-center space-y-4">
        <h1 className="text-2xl font-bold">Something went wrong</h1>
        <p className="text-muted-foreground">An unexpected error occurred. Please refresh the page.</p>
        <button onClick={() => window.location.reload()} className="px-4 py-2 bg-primary text-primary-foreground rounded-lg">
          Refresh
        </button>
      </div>
    </div>
  );
}

function App() {
  return (
    <Sentry.ErrorBoundary fallback={SentryFallback}>
      <HelmetProvider>
        <QueryClientProvider client={queryClient}>
          <TooltipProvider>
            <WouterRouter base={import.meta.env.BASE_URL.replace(/\/$/, "")}>
              <AppRouter />
            </WouterRouter>
            <Toaster />
          </TooltipProvider>
        </QueryClientProvider>
      </HelmetProvider>
    </Sentry.ErrorBoundary>
  );
}

export default App;
